import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import profileAvatarSvg from '@/assets/profile-avatar.svg';
import { CONFIG } from '@/const/config';
import {
  PhWallet,
  PhPaperPlaneTilt,
  PhCreditCard,
  PhArrowsDownUp,
  PhGear,
  PhCaretRight,
} from '@phosphor-icons/vue';

interface NavItem {
  name: string;
  label: string;
  route: string;
  icon?: any;
  svg?: string;
  children?: NavItem[];
}

interface MobileNavItem extends NavItem {
  activeKey: 'dashboard' | 'wallet' | 'trade';
}

interface Props {
  navItems?: NavItem[];
  footerItems?: NavItem[];
  userAvatar?: string;
  chatUnread?: number;
}

const mobileDashboardSvg = '<svg viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10.0625 5.25H17.9375" stroke="currentColor" stroke-width="1.5" stroke-linecap="square"/><path d="M7 9.625H21" stroke="currentColor" stroke-width="1.5" stroke-linecap="square"/><path d="M5.25 14C5.25 13.2751 5.83763 12.6875 6.5625 12.6875H21.4375C22.1624 12.6875 22.75 13.2751 22.75 14V23.1875C22.75 23.9124 22.1624 24.5 21.4375 24.5H6.5625C5.83763 24.5 5.25 23.9124 5.25 23.1875V14Z" stroke="currentColor" stroke-width="1.5"/></svg>';
const mobileWalletSvg = '<svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="11.375" cy="16.625" r="8" stroke="currentColor" stroke-width="1.5"/><mask id="path-2-inside-1_6351_51846" fill="white"><path fill-rule="evenodd" clip-rule="evenodd" d="M21.5721 14.1024C22.9675 13.1048 23.8771 11.4711 23.8771 9.625C23.8771 6.58743 21.4146 4.125 18.3771 4.125C16.5309 4.125 14.8973 5.03456 13.8997 6.43C13.3778 6.30128 12.8404 6.2116 12.291 6.16423C13.4957 4.05031 15.7699 2.625 18.3771 2.625C22.2431 2.625 25.3771 5.75901 25.3771 9.625C25.3771 12.2321 23.9518 14.5064 21.8378 15.711C21.7905 15.1616 21.7008 14.6243 21.5721 14.1024Z"/></mask><path d="M21.5721 14.1024L21.0632 13.3905L20.5804 13.7357L20.7225 14.3119L21.5721 14.1024ZM13.8997 6.43L13.6902 7.27955L14.2664 7.42165L14.6115 6.93886L13.8997 6.43ZM12.291 6.16423L11.5308 5.731L10.854 6.91857L12.2158 7.03599L12.291 6.16423ZM21.8378 15.711L20.9661 15.7862L21.0835 17.148L22.2711 16.4713L21.8378 15.711ZM22.0809 14.8142C23.6963 13.6594 24.7521 11.7655 24.7521 9.625H23.0021C23.0021 11.1767 22.2387 12.5502 21.0632 13.3905L22.0809 14.8142ZM24.7521 9.625C24.7521 6.10418 21.8979 3.25 18.3771 3.25V5C20.9314 5 23.0021 7.07068 23.0021 9.625H24.7521ZM18.3771 3.25C16.2366 3.25 14.3426 4.30579 13.1879 5.92115L14.6115 6.93886C15.4519 5.76333 16.8253 5 18.3771 5V3.25ZM12.2158 7.03599C12.7197 7.07943 13.2121 7.16165 13.6902 7.27955L14.1092 5.58046C13.5434 5.44092 12.9612 5.34376 12.3662 5.29246L12.2158 7.03599ZM18.3771 1.75C15.4429 1.75 12.8847 3.35512 11.5308 5.731L13.0512 6.59745C14.1066 4.7455 16.0969 3.5 18.3771 3.5V1.75ZM26.2521 9.625C26.2521 5.27576 22.7263 1.75 18.3771 1.75V3.5C21.7598 3.5 24.5021 6.24226 24.5021 9.625H26.2521ZM22.2711 16.4713C24.6469 15.1173 26.2521 12.5592 26.2521 9.625H24.5021C24.5021 11.9051 23.2566 13.8955 21.4046 14.9508L22.2711 16.4713ZM20.7225 14.3119C20.8404 14.79 20.9226 15.2824 20.9661 15.7862L22.7096 15.6359C22.6583 15.0409 22.5611 14.4587 22.4216 13.8928L20.7225 14.3119Z" fill="currentColor" mask="url(#path-2-inside-1_6351_51846)"/></svg>';


export default /*@__PURE__*/_defineComponent({
  __name: 'FloatingSidebar',
  props: {
    navItems: { type: Array, required: false, default: undefined },
    footerItems: { type: Array, required: false, default: undefined },
    userAvatar: { type: String, required: false },
    chatUnread: { type: Number, required: false }
  },
  emits: ["help-clicked", "profile-clicked"],
  setup(__props: any, { expose: __expose }) {
  __expose();

const sidebarRef = ref<HTMLElement | null>(null);
const isExpanded = ref(false);

const toggleSidebar = () => {
  isExpanded.value = !isExpanded.value;
};

const onClickOutside = (e: MouseEvent) => {
  if (isExpanded.value && sidebarRef.value && !sidebarRef.value.contains(e.target as Node)) {
    isExpanded.value = false;
  }
};

onMounted(() => {
  document.addEventListener('click', onClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', onClickOutside);
});

const props = __props;

const { t } = useI18n();

const computedNavItems = computed(() => props.navItems ?? [
  { name: 'dashboard', label: t('nav.dashboard'), route: '/app/dashboard', icon: PhWallet },
  { name: 'accounts', label: t('nav.accounts'), route: '/app/accounts', svg: '<svg width="24" height="24" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="13" cy="19" r="9" stroke="currentColor" stroke-width="2"/><mask id="path-2-inside-1_489_3849" fill="white"><path d="M21 3C25.4183 3 29 6.58172 29 11C29 13.9795 27.3698 16.5754 24.9541 17.9521C24.8908 17.2205 24.76 16.5085 24.5713 15.8203C26.0447 14.727 27 12.9756 27 11C27 7.68629 24.3137 5 21 5C19.0246 5.00012 17.2719 5.95459 16.1787 7.42773C15.4903 7.23912 14.7778 7.10807 14.0459 7.04492C15.4226 4.62932 18.0207 3.00014 21 3Z"/></mask><path d="M21 3V2H21L21 3ZM24.9541 17.9521L23.9578 18.0383L24.0924 19.5942L25.4493 18.821L24.9541 17.9521ZM24.5713 15.8203L23.9754 15.0172L23.426 15.4249L23.6069 16.0847L24.5713 15.8203ZM21 5V4H20.9999L21 5ZM16.1787 7.42773L15.9145 8.39219L16.5741 8.57293L16.9817 8.02366L16.1787 7.42773ZM14.0459 7.04492L13.1771 6.54976L12.4036 7.90694L13.9599 8.04122L14.0459 7.04492ZM21 3V4C24.866 4 28 7.13401 28 11H29H30C30 6.02944 25.9706 2 21 2V3ZM29 11H28C28 13.605 26.576 15.8768 24.4589 17.0833L24.9541 17.9521L25.4493 18.821C28.1637 17.2739 30 14.3539 30 11H29ZM24.9541 17.9521L25.9504 17.866C25.8817 17.0718 25.7397 16.3001 25.5357 15.5559L24.5713 15.8203L23.6069 16.0847C23.7802 16.7168 23.8999 17.3692 23.9578 18.0383L24.9541 17.9521ZM24.5713 15.8203L25.1672 16.6234C26.8835 15.3498 28 13.306 28 11H27H26C26 12.6453 25.2058 14.1042 23.9754 15.0172L24.5713 15.8203ZM27 11H28C28 7.13401 24.866 4 21 4V5V6C23.7614 6 26 8.23858 26 11H27ZM21 5L20.9999 4C18.6948 4.00014 16.6495 5.1153 15.3757 6.8318L16.1787 7.42773L16.9817 8.02366C17.8944 6.79388 19.3545 6.0001 21.0001 6L21 5ZM16.1787 7.42773L16.443 6.46328C15.6987 6.25937 14.9266 6.11719 14.1319 6.04862L14.0459 7.04492L13.9599 8.04122C14.629 8.09895 15.2819 8.21887 15.9145 8.39219L16.1787 7.42773ZM14.0459 7.04492L14.9147 7.54008C16.1209 5.42363 18.3947 4.00012 21 4L21 3L21 2C17.6468 2.00016 14.7243 3.835 13.1771 6.54976L14.0459 7.04492Z" fill="currentColor" mask="url(#path-2-inside-1_489_3849)"/></svg>' },
  { name: 'payments', label: t('nav.transactions'), route: '/app/payments', icon: PhPaperPlaneTilt, children: [
    { name: 'my-trades', label: t('trading.myTrades'), route: '/app/trades' },
  ] },
  // { name: 'cards', label: t('nav.cards'), route: '/app/cards', icon: PhCreditCard },
  // { name: 'crypto', label: t('nav.crypto'), route: '/app/crypto', svg: '...' },
  { name: 'trading', label: t('nav.trading'), route: '/app/crypto/trading', svg: '<svg width="29" height="28" viewBox="0 0 29 28" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4 7C5.10457 7 6 7.89543 6 9V16C6 17.1046 5.10457 18 4 18H3C1.89543 18 1 17.1046 1 16V9C1 7.89543 1.89543 7 3 7H4Z" stroke="currentColor" stroke-width="2"/><path d="M15 10C16.1046 10 17 10.8954 17 12V22C17 23.1046 16.1046 24 15 24H14C12.8954 24 12 23.1046 12 22V12C12 10.8954 12.8954 10 14 10H15Z" stroke="currentColor" stroke-width="2"/><path d="M26 5C27.1046 5 28 5.89543 28 7V16C28 17.1046 27.1046 18 26 18H25C23.8954 18 23 17.1046 23 16V7C23 5.89543 23.8954 5 25 5H26Z" stroke="currentColor" stroke-width="2"/><line x1="14.5" y1="10" x2="14.5" y2="7" stroke="currentColor" stroke-width="2"/><line x1="3.5" y1="7" x2="3.5" y2="2" stroke="currentColor" stroke-width="2"/><line x1="3.5" y1="23" x2="3.5" y2="18" stroke="currentColor" stroke-width="2"/><line x1="25.5" y1="5" x2="25.5" stroke="currentColor" stroke-width="2"/><line x1="25.5" y1="23" x2="25.5" y2="18" stroke="currentColor" stroke-width="2"/><line x1="14.5" y1="28" x2="14.5" y2="24" stroke="currentColor" stroke-width="2"/></svg>' },
  { name: 'renting', label: t('nav.renting'), route: '/app/renting', svg: '<svg width="24" height="24" viewBox="0 0 256 256" fill="none" xmlns="http://www.w3.org/2000/svg"><polyline points="232 56 136 152 96 112 24 184" fill="none" stroke="currentColor" stroke-width="16" stroke-linecap="round" stroke-linejoin="round"></polyline><polyline points="232 120 232 56 168 56" fill="none" stroke="currentColor" stroke-width="16" stroke-linecap="round" stroke-linejoin="round"></polyline></svg>' },
  ...(CONFIG.LOAN_MODULE_ALLOWED ? [
    { name: 'loans', label: t('nav.loans'), route: '/app/loans', svg: '<svg width="24" height="24" viewBox="0 0 256 256" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="24" y="56" width="208" height="144" rx="16" fill="none" stroke="currentColor" stroke-width="16"/><line x1="24" y1="96" x2="232" y2="96" stroke="currentColor" stroke-width="16" stroke-linecap="round"/><line x1="56" y1="160" x2="112" y2="160" stroke="currentColor" stroke-width="16" stroke-linecap="round"/></svg>' },
  ] : []),
]);

const computedFooterItems = computed(() => props.footerItems ?? []);

const mobileBottomItems = computed<MobileNavItem[]>(() => {
  const navItems = computedNavItems.value;
  const byName = (name: string) => navItems.find(item => item.name === name);
  const wallet = byName('accounts') ?? { name: 'accounts', label: t('nav.accounts'), route: '/app/accounts', icon: PhWallet };
  const trade = byName('trading') ?? { name: 'trading', label: t('nav.trading'), route: '/app/crypto/trading', icon: PhWallet };

  return [
    {
      name: 'dashboard',
      label: 'Dashboard',
      route: '/app/dashboard',
      svg: mobileDashboardSvg,
      activeKey: 'dashboard',
    },
    {
      ...wallet,
      label: 'Wallet',
      svg: mobileWalletSvg,
      activeKey: 'wallet',
    },
    {
      ...trade,
      label: 'Trade',
      activeKey: 'trade',
    },
  ];
});

const mobileProductSections = computed(() => {
  const navItems = computedNavItems.value;
  const byName = (name: string) => navItems.find(item => item.name === name);
  const payments = byName('payments');
  const trades = payments?.children?.find(item => item.name === 'my-trades');
  const renting = byName('renting');
  const tradesItem: NavItem = {
    ...(trades ?? { name: 'my-trades', route: '/app/trades' }),
    label: 'Trades',
    icon: PhArrowsDownUp,
  };
  const settingsItem: NavItem = {
    name: 'settings',
    label: 'Settings',
    route: '/app/settings',
    icon: PhGear,
  };

  return [
    {
      label: 'Crypto',
      items: [renting].filter(Boolean) as NavItem[],
    },
    {
      label: 'Money',
      items: [payments, tradesItem, settingsItem].filter(Boolean) as NavItem[],
    },
  ];
});



const router = useRouter();
const route = useRoute();

const moreSheetOpen = ref(false);

const isMobileNavActive = (section: 'dashboard' | 'wallet' | 'trade') => {
  if (section === 'dashboard') {
    return route.path === '/app/dashboard';
  }
  if (section === 'wallet') {
    return route.path === '/app/accounts' || route.path.startsWith('/app/account/');
  }
  return route.path === '/app/crypto/trading' || route.path.startsWith('/app/crypto/trading/');
};

const toggleMoreSheet = () => {
  moreSheetOpen.value = !moreSheetOpen.value;
};

const moreNavigate = (path: string) => {
  moreSheetOpen.value = false;
  router.push(path);
};

const navigateTo = (path: string) => {
  router.push(path);
};

const isActive = (path: string) => {
  if (route.path === path) return true;
  if (!route.path.startsWith(path + '/')) return false;
  // If a more specific nav route also matches, this parent should not be active
  const navPaths = [...computedNavItems.value, ...computedFooterItems.value].map(i => i.route);
  const hasMoreSpecificMatch = navPaths.some(p => p !== path && p.startsWith(path + '/') && route.path.startsWith(p));
  return !hasMoreSpecificMatch;
};


const __returned__ = { sidebarRef, isExpanded, toggleSidebar, onClickOutside, props, t, mobileDashboardSvg, mobileWalletSvg, computedNavItems, computedFooterItems, mobileBottomItems, mobileProductSections, router, route, moreSheetOpen, isMobileNavActive, toggleMoreSheet, moreNavigate, navigateTo, isActive, get profileAvatarSvg() { return profileAvatarSvg }, get PhCaretRight() { return PhCaretRight } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})